import { useState, useRef, useCallback } from 'react';

/**
 * Obtiene el identificador único del item: preferir `key` (canonical del backend), sino `jobName`.
 */
function getItemId(item) {
  return (item.key && item.key.trim()) ? item.key : item.jobName;
}

/**
 * Hook para persistir la selección de llamadas entre páginas.
 * Usa un Map interno con key (del backend) o jobName como clave.
 * Almacena siempre la prop `key` cuando el backend la devuelve.
 */
export function useSelectionPersistence() {
  const mapRef = useRef(new Map());
  const [totalSelectedCount, setTotalSelectedCount] = useState(0);
  // feature 025 (US8): modo «todas las del filtro». Mientras está activo el Map queda vacío (no se
  // ve ninguna fila marcada) y cualquier cambio de selección lo apaga.
  const allFilteredRef = useRef(false);
  const [allFiltered, setAllFilteredState] = useState(false);

  const syncCount = useCallback(() => {
    setTotalSelectedCount(mapRef.current.size);
  }, []);

  const exitAllFiltered = useCallback(() => {
    if (allFilteredRef.current) {
      allFilteredRef.current = false;
      setAllFilteredState(false);
    }
  }, []);

  const setAllFiltered = useCallback((value) => {
    const next = Boolean(value);
    if (next) {
      mapRef.current.clear();
      syncCount();
    }
    allFilteredRef.current = next;
    setAllFilteredState(next);
  }, [syncCount]);

  const addItems = useCallback((items) => {
    exitAllFiltered();
    let changed = false;
    for (const item of items) {
      const id = getItemId(item);
      if (!id) continue;
      if (!mapRef.current.has(id)) {
        mapRef.current.set(id, {
          id: item.id,
          jobName: item.jobName,
          key: item.key || null,
          supervisor: item.supervisor,
          agent: item.agent,
          timestamp: item.timestamp,
        });
        changed = true;
      }
    }
    if (changed) syncCount();
  }, [syncCount, exitAllFiltered]);

  const removeItems = useCallback((items) => {
    exitAllFiltered();
    let changed = false;
    for (const item of items) {
      const id = getItemId(item);
      if (id && mapRef.current.has(id)) {
        mapRef.current.delete(id);
        changed = true;
      }
    }
    if (changed) syncCount();
  }, [syncCount, exitAllFiltered]);

  const clearAll = useCallback(() => {
    exitAllFiltered();
    if (mapRef.current.size > 0) {
      mapRef.current.clear();
      syncCount();
    }
  }, [syncCount, exitAllFiltered]);

  /**
   * Cambio de filtro (fechas/supervisor/agentes): vacía la selección individual, que ya no
   * corresponde al filtro nuevo, pero **conserva** el modo «todas las del filtro» (FR-045: con el
   * gesto activo, generar usa el filtro vigente).
   */
  const resetForFilterChange = useCallback(() => {
    if (mapRef.current.size > 0) {
      mapRef.current.clear();
      syncCount();
    }
  }, [syncCount]);

  /**
   * Devuelve las keys seleccionadas para enviar al backend (selectedJobNames).
   * El backend filtra solo por key (no por jobName).
   */
  const getSelectedJobNames = useCallback(() => {
    return Array.from(mapRef.current.values()).map(v => v.key).filter(Boolean);
  }, []);

  /**
   * Dado los items de la página actual, devuelve los que están seleccionados
   * en el Map global. Esto se usa como prop `selectedItems` de CloudScape Table.
   */
  const getPageSelectedItems = useCallback((pageItems) => {
    if (!pageItems) return [];
    return pageItems.filter(item => {
      const id = getItemId(item);
      return id && mapRef.current.has(id);
    });
  }, []);

  /**
   * Reconcilia un evento onSelectionChange de CloudScape Table con el Map global.
   * `newPageSelected` son los items que CloudScape reporta como seleccionados
   * en la página actual. `currentPageItems` son todos los items de la página.
   */
  const handleSelectionChange = useCallback((newPageSelected, currentPageItems) => {
    if (!currentPageItems) return;
    // Con el modo activo el Map ya está vacío: la reconciliación normal deja como única
    // selección las filas que se acaban de marcar.
    exitAllFiltered();

    const newSelectedIds = new Set(newPageSelected.map(i => getItemId(i)));
    let changed = false;

    for (const item of currentPageItems) {
      const id = getItemId(item);
      if (!id) continue;

      if (newSelectedIds.has(id)) {
        if (!mapRef.current.has(id)) {
          mapRef.current.set(id, {
            id: item.id,
            jobName: item.jobName,
            key: item.key || null,
            supervisor: item.supervisor,
            agent: item.agent,
            timestamp: item.timestamp,
          });
          changed = true;
        }
      } else {
        if (mapRef.current.has(id)) {
          mapRef.current.delete(id);
          changed = true;
        }
      }
    }

    if (changed) syncCount();
  }, [syncCount, exitAllFiltered]);

  return {
    mapRef,
    totalSelectedCount,
    allFiltered,
    setAllFiltered,
    resetForFilterChange,
    addItems,
    removeItems,
    clearAll,
    getSelectedJobNames,
    getPageSelectedItems,
    handleSelectionChange,
  };
}
